import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import { NumberStepper } from '../components/NumberStepper';
import {
  getTicketTypes,
  listProductComponents,
  listSchedules,
  type ProductSummary,
  type ScheduleSummary,
  type SellableTicketType,
} from '../lib/api';
import { formatPrice, formatTime, pickI18n } from '../lib/format';
import { dayLabel, groupByDay } from '../lib/schedule-group';
import { useKiosk, type PlanItem, type PlanLeg } from '../lib/session';
import { ticketTypeLabel } from '../lib/ticket-label';

/** 票種顯示順序；與後端 PricingResolver 的 TICKET_TYPE_DISPLAY_ORDER 一致。 */
const TICKET_TYPE_ORDER = [
  'adult',
  'senior',
  'child',
  'child_6_11',
  'child_3_5',
  'infant',
  'early_bird',
  'package_family',
  'custom',
];

/** 班次可售：未天候取消且尚有座位。 */
function isPurchasable(s: ScheduleSummary): boolean {
  return !s.weather_cancelled && s.capacity - s.reserved_count > 0;
}

function overlaps(a: ScheduleSummary, b: ScheduleSummary): boolean {
  return (
    new Date(a.departure_at) < new Date(b.departure_end_at) &&
    new Date(b.departure_at) < new Date(a.departure_end_at)
  );
}

/** 這一段要選班次的商品（套票是它的組成，單買是它自己）。 */
interface Slot {
  /** 所屬項目在 planProducts 中的索引——同一項目的段共用票種與價格 */
  itemIndex: number;
  product: ProductSummary;
}

/**
 * 逐段選時段 — 套票與多商品行程共用。
 *
 * ## 為什麼兩件事共用一頁
 *
 * 套票（一個商品、多段）與行程推薦的多商品組合（多個商品、各一段）在顧客眼裡是
 * 同一件事：「我要走這幾段，各挑一個時間」。差別只在錢怎麼算——套票是打包價，
 * 多商品是各自的牌價相加。把它們分成兩頁只會讓同樣的時段挑選邏輯寫兩份。
 *
 * ## 這一頁修掉的 bug
 *
 * 行程推薦推出多商品組合，但「立即訂購」原本只帶走 `products[0]`，其餘無聲消失：
 * 顧客看到三小時的行程、按下訂購、買到一小時的東西，而且畫面上不會有任何異狀。
 *
 * ## 人數只選一次
 *
 * 同一群人走完全程，各段人數本來就該一致（套票後端也是這樣驗的）。
 */
export function PlanScheduleScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, draft, patchDraft } = useKiosk();
  const products = draft.planProducts;

  /** slot 序號 → 選定的 schedule_id。 */
  const [picked, setPicked] = useState<Record<number, string>>({});
  const [quantities, setQuantities] = useState<Record<string, number>>(draft.quantities);

  /*
   * 套票要先查組成才知道有幾段；非套票沒有這一步。用固定長度的 useQueries
   * （每個項目一筆）讓 hook 數量只隨 planProducts 變動，而那在本頁生命週期內不變。
   */
  const componentQueries = useQueries({
    queries: (products ?? []).map((p) => ({
      queryKey: ['product-components', p.id],
      queryFn: () => (p.category === 'package' ? listProductComponents(p.id) : Promise.resolve([])),
      staleTime: 60_000,
    })),
  });

  // 展開成 slot：套票→它的組成，非套票→它自己
  const slots: Slot[] = (products ?? []).flatMap((p, itemIndex) => {
    if (p.category !== 'package') return [{ itemIndex, product: p }];
    const comps = componentQueries[itemIndex]?.data ?? [];
    return comps.map((c) => ({
      itemIndex,
      product: {
        id: c.component_product_id,
        sku: c.sku,
        category: 'component',
        name_i18n: c.name_i18n,
        description_i18n: c.description_i18n,
        base_price: c.base_price,
        currency: 'TWD',
        duration_minutes: c.duration_minutes,
        channels: ['kiosk'],
        is_published: true,
      } satisfies ProductSummary,
    }));
  });

  const scheduleQueries = useQueries({
    queries: slots.map((slot) => ({
      queryKey: ['schedules', slot.product.id],
      queryFn: () => listSchedules(slot.product.id),
      staleTime: 30_000,
    })),
  });

  const chosen: Array<ScheduleSummary | null> = slots.map((_, i) => {
    const id = picked[i];
    if (id === undefined) return null;
    return scheduleQueries[i]?.data?.items.find((s) => s.id === id) ?? null;
  });
  const allPicked = slots.length > 0 && chosen.every((s) => s !== null);

  /*
   * 票種與單價逐項目查：套票不帶 schedule_id（它沒有班次，後端只回商品價目表），
   * 非套票要帶（規則掛在 (product, schedule) 上）。全部選完才查——非套票在選定
   * 班次前根本問不出價格。
   */
  const ticketTypeQueries = useQueries({
    queries: (products ?? []).map((p, itemIndex) => {
      const firstSlot = slots.findIndex((s) => s.itemIndex === itemIndex);
      const schedule = firstSlot === -1 ? null : chosen[firstSlot];
      return {
        queryKey: ['ticket-types', p.id, schedule?.id ?? 'package'],
        queryFn: () =>
          getTicketTypes({
            product_id: p.id,
            channel: 'kiosk',
            ...(p.category === 'package' ? {} : { schedule_id: schedule?.id ?? '' }),
          }),
        enabled: allPicked && (p.category === 'package' || schedule !== null),
        staleTime: 30_000,
      };
    }),
  });

  if (products === null || products.length === 0) return <Navigate to="/home" replace />;

  const perItemTypes: SellableTicketType[][] = (products ?? []).map(
    (_, i) => ticketTypeQueries[i]?.data?.ticket_types ?? [],
  );

  /*
   * 票種取聯集而非交集：三小時的行程可能一段有兒童票、另一段沒有。沒有的那一段
   * 後端會退回該商品的成人價（`perTypeBasePrice` 的 fallback），孩子照樣上得去，
   * 只是那一段不打折。取交集會讓兒童票整個從畫面消失，等於逼家長買成人票卻拿不到
   * 有兒童價那一段的折扣。
   */
  const sellableTypes = [...new Set(perItemTypes.flat().map((t2) => t2.ticket_type))].sort(
    (a, b) => rank(a) - rank(b),
  );

  /** 該票種的整趟單價 = 各項目該票種單價之和（該項目沒有此票種時退回它的成人價）。 */
  const unitPriceOf = (ticketType: string): number =>
    perItemTypes.reduce((sum, types) => {
      const exact = types.find((x) => x.ticket_type === ticketType);
      if (exact) return sum + Number(exact.unit_price);
      const adult = types.find((x) => x.ticket_type === 'adult');
      return sum + (adult ? Number(adult.unit_price) : 0);
    }, 0);

  const qtyOf = (tt: string): number => quantities[tt] ?? 0;
  const partySize = sellableTypes.reduce((sum, tt) => sum + qtyOf(tt), 0);

  /*
   * 可買人數上限 = 所有段落剩餘座位的最小值。任何一段滿了，整趟就走不完。
   */
  const seatCap = allPicked
    ? Math.min(
        ...chosen.map(
          (s) => (s as ScheduleSummary).capacity - (s as ScheduleSummary).reserved_count,
        ),
      )
    : 0;

  const totalNum = sellableTypes.reduce((sum, tt) => sum + unitPriceOf(tt) * qtyOf(tt), 0);
  const pricesReady = allPicked && perItemTypes.every((types) => types.length > 0);
  const canProceed =
    allPicked && pricesReady && partySize >= 1 && partySize <= seatCap && qtyOf('adult') >= 1;

  function proceed(): void {
    if (!canProceed || products === null) return;
    const cleaned: Record<string, number> = {};
    for (const tt of sellableTypes) {
      const q = qtyOf(tt);
      if (q > 0) cleaned[tt] = q;
    }
    const plan: PlanItem[] = products.map((p, itemIndex) => ({
      product: p,
      legs: slots
        .map((slot, i): PlanLeg | null =>
          slot.itemIndex === itemIndex && chosen[i] !== null
            ? { product: slot.product, schedule: chosen[i] as ScheduleSummary }
            : null,
        )
        .filter((l): l is PlanLeg => l !== null),
    }));
    /*
     * `product` / `schedule` 仍要填：下游畫面（取票／付款摘要／出票完成）與它們的
     * 導頁守門都讀這兩個欄位。填最早出發的那一段——顧客問的是「幾點要到」。
     */
    const earliest = [...chosen]
      .filter((s): s is ScheduleSummary => s !== null)
      .sort((a, b) => new Date(a.departure_at).getTime() - new Date(b.departure_at).getTime())[0];
    patchDraft({
      plan,
      planTotal: totalNum,
      product: products[0] ?? null,
      schedule: earliest ?? null,
      quantities: cleaned,
      quote: null,
    });
    navigate('/delivery');
  }

  const loadingComponents = componentQueries.some((q) => q.isPending);

  return (
    <KioskFrame bleed>
      <HeroBanner height={340} align="left">
        <Mascot size={210} pose="smile" />
      </HeroBanner>

      <h1 className="k-h1" style={{ textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
        {products.map((p) => pickI18n(p.name_i18n, p.sku, locale)).join(' + ')}
      </h1>
      <p className="k-subtitle k-subtitle--flanked">{t('plan.lead')}</p>

      {loadingComponents && <p className="k-text k-column">{t('common.loading')}</p>}
      {!loadingComponents && slots.length === 0 && (
        <p className="k-error k-column">{t('plan.notSellable')}</p>
      )}

      {slots.map((slot, i) => {
        const query = scheduleQueries[i];
        const items = (query?.data?.items ?? []).filter(isPurchasable);
        // 其他段已選的時間；與之重疊者不給選——同一個人不可能同時在兩個地方
        const others = chosen.filter((s, idx): s is ScheduleSummary => idx !== i && s !== null);
        return (
          <div key={`${slot.itemIndex}-${slot.product.id}`} className="k-column">
            {/*
             * 時長不放進 h2：`.k-sectionhead` 是 flex + 大字級的單行標題，商品名一長
             * （「《安平港灣巡禮》遊艇之旅」）再塞一段括號就會把行高撐爆、疊到下一段。
             */}
            <h2 className="k-sectionhead">
              {t('plan.segment', { n: i + 1 })}｜
              {pickI18n(slot.product.name_i18n, slot.product.sku, locale)}
            </h2>
            {slot.product.duration_minutes !== null && (
              <p className="k-text k-text--dim" style={{ marginTop: -14, marginBottom: 14 }}>
                {slot.product.duration_minutes} {t('browse.minutes')}
              </p>
            )}
            {query?.isPending === true && <p className="k-text">{t('common.loading')}</p>}
            {query?.isSuccess === true && items.length === 0 && (
              <p className="k-text k-text--dim">{t('plan.noSlot')}</p>
            )}
            {/*
             * 日期標頭不可省：同一個時段每天都有一班，少了它畫面上就是二十個一模一樣的
             * 「10:00 - 11:00」，顧客沒有任何辦法分辨自己按的是哪一天。
             */}
            {groupByDay(items).map(([dayKey, daySchedules]) => (
              <div key={dayKey}>
                <h3 className="k-dayhead">{dayLabel(dayKey, locale, t)}</h3>
                <div className="k-slots">
                  {daySchedules.map((s) => {
                    const clash = others.some((o) => overlaps(o, s));
                    const active = picked[i] === s.id;
                    const remaining = s.capacity - s.reserved_count;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`k-slot${active ? ' k-slot--active' : ''}`}
                        aria-pressed={active}
                        disabled={clash && !active}
                        onClick={() => setPicked((p) => ({ ...p, [i]: s.id }))}
                      >
                        <span className="k-slot__time">
                          {formatTime(s.departure_at)} - {formatTime(s.departure_end_at)}
                        </span>
                        <hr className="k-slot__rule" />
                        <span className="k-slot__seats">
                          {clash && !active ? (
                            t('plan.clash')
                          ) : (
                            <>
                              {t('schedule.remaining')}
                              <span className="k-slot__num">{remaining}</span>
                              <span className="k-slot__cap">/{s.capacity}</span>
                            </>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      <hr className="k-hairline" />

      <h2 className="k-sectionhead k-column">{t('schedule.partyTitle')}</h2>
      <div className="k-column">
        {!allPicked && <p className="k-text k-text--dim">{t('plan.pickEach')}</p>}
        {allPicked && !pricesReady && <p className="k-text">{t('common.loading')}</p>}
        {allPicked && pricesReady && (
          <p className="k-text k-text--dim">{t('plan.remainingLimit', { n: seatCap })}</p>
        )}
        {pricesReady &&
          sellableTypes.map((tt) => {
            const unit = unitPriceOf(tt);
            /*
             * 上限綁「本票種現有人數 + 還能再加幾人」。各票種各給一個固定值的話，
             * 大人 20 + 兒童 20 就能點出 40 個人，剩餘只有 3 也照點。
             */
            const max = Math.max(0, qtyOf(tt) + (seatCap - partySize));
            return (
              <NumberStepper
                key={tt}
                label={`${ticketTypeLabel(tt, null, locale)}（${formatPrice(unit, 'TWD')}）`}
                value={qtyOf(tt)}
                min={tt === 'adult' ? 1 : 0}
                max={max}
                onChange={(v) => setQuantities((q) => ({ ...q, [tt]: v }))}
              />
            );
          })}

        {pricesReady && sellableTypes.length > 0 && (
          <>
            <hr className="k-hairline" style={{ margin: '18px 0' }} />
            <div className="k-row">
              <span className="k-text" style={{ fontWeight: 700 }}>
                {t('schedule.subtotal')}（{partySize}）
              </span>
              <span className="k-amount k-amount--primary">{formatPrice(totalNum, 'TWD')}</span>
            </div>
          </>
        )}
      </div>

      <div className="k-footbar k-footbar--center">
        <KioskButton variant="secondary" onClick={() => navigate(-1)}>
          ← {t('common.back')}
        </KioskButton>
        <KioskButton variant="primary" disabled={!canProceed} onClick={proceed}>
          {t('schedule.toDelivery')} →
        </KioskButton>
      </div>
    </KioskFrame>
  );
}

function rank(t: string): number {
  const i = TICKET_TYPE_ORDER.indexOf(t);
  return i === -1 ? 99 : i;
}
